import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { routeMetaMap, RouteMetaConfig } from '../config/routeMetaConfig';

/**
 * Hook that automatically sets Open Graph meta tags based on the current route
 * 
 * @param overrides - Optional overrides for specific routes or to disable route-based meta
 * @param enabled - Set to false to disable route-based meta updates (default: true)
 */
const useRouteMeta = (
  overrides?: Partial<RouteMetaConfig> | null,
  enabled: boolean = true
) => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;

    // Find matching route config
    // First try exact match, then try prefix match for dynamic routes
    let routeConfig: RouteMetaConfig | undefined;
    
    // Exact match
    if (routeMetaMap[location.pathname]) {
      routeConfig = routeMetaMap[location.pathname];
    } else {
      // Try to find a matching route prefix (for dynamic routes like /people/:id)
      const pathSegments = location.pathname.split('/').filter(Boolean);
      for (let i = pathSegments.length; i > 0; i--) {
        const testPath = '/' + pathSegments.slice(0, i).join('/');
        if (routeMetaMap[testPath]) {
          routeConfig = routeMetaMap[testPath];
          break;
        }
      }
    }

    // Use default if no match found
    const config = routeConfig || routeMetaMap['/'];
    
    // Apply overrides if provided
    const finalConfig = overrides ? { ...config, ...overrides } : config;

    // Update meta tags
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      
      metaTag.setAttribute('content', content);
    };

    // Update og:image if provided
    if (finalConfig.image) {
      updateMetaTag('og:image', finalConfig.image);
      updateMetaTag('twitter:image', finalConfig.image);
    }

    // Update og:title if provided
    if (finalConfig.title) {
      updateMetaTag('og:title', finalConfig.title);
      updateMetaTag('twitter:title', finalConfig.title);
    }

    // Update og:description if provided
    if (finalConfig.description) {
      updateMetaTag('og:description', finalConfig.description);
      updateMetaTag('twitter:description', finalConfig.description);
    }

    // Update og:image dimensions if provided
    if (finalConfig.imageWidth) {
      updateMetaTag('og:image:width', finalConfig.imageWidth.toString());
    }
    if (finalConfig.imageHeight) {
      updateMetaTag('og:image:height', finalConfig.imageHeight.toString());
    }

    // Always update og:url to current location
    const currentUrl = window.location.origin + location.pathname + location.search;
    updateMetaTag('og:url', currentUrl);
  }, [location.pathname, location.search, overrides, enabled]);
};

export default useRouteMeta;
export { routeMetaMap };
export type { RouteMetaConfig };

